import $ from 'jquery';
import {
	doRTPostback,
	getCurrentPageName,
	getOrdersList,
	initImagesData,
	initKount,
	appendShippingInfo,
	isIndex,
	setDefaultBonusActions,
	clearCookies,
	shipValue,
} from './actions';
import {
	setStates,
} from './international';
import {
	konnektive_finalize_orders,
	konnektive_get_images,
	doPresellPage,
	doLeadPage,
	doCheckoutPage,
	konnektive_maintain_lead,
} from './api';
import {
	useCheckoutGateway,
	clearGatewaySession,
	clearOrderIdempotencyKey,
} from './checkoutGateway';
import {
	UTILS_CSS,
	MONTHS_BILL_SELECT,
} from './constants';
import { isSapphireMode, isSapphireThankYouPage } from './sapphire';
import {
	getSapphireCart,
	sapphireCartOutput,
	sapphireDisplayProducts,
	bindSapphireAddToCartButton,
} from './sapphireCart';
import {
	getCookieValue,
	deleteSessionStorage,
	getConfig,
	getSessionStorage,
	setConfig,
	setCookieValue,
	setSessionStorage,
} from './config';
import {
	debug,
	resetFormState,
	getQueryParamValue,
	resetDebugCounter,
	getRTDomain,
} from './utils';
import {
	firePostBack,
} from './tracking';
import { getBonusNumber, getFunnelScope } from './routing';
import { HANDOFF_STORAGE_KEY } from './pii';

// Engine-format OPconfig files declare a page-level `const DEBUG = 0;`, but
// konnektive-core configs (1.5.1) do not. Reading the bare global then threw
// a ReferenceError inside render() — on bonus1's first view (no upsell
// handlers) and on payment pages reached with a clickid — so fall back to the
// config's debugMode when the page never declared DEBUG.
function pageDebugFlag() {
	// eslint-disable-next-line no-undef
	return typeof DEBUG !== 'undefined' ? DEBUG : getConfig().debugMode;
}

// Apply WHATWG autocomplete hints to the checkout fields within a form.
// Without given-name/family-name, heuristic browser/password-manager autofill
// can drop the full name into BOTH name fields (e.g. "Jane Doe Jane Doe"),
// which then propagates to the order. Applied centrally so every site gets it.
// RedTrack postbacks fire only on the FIRST view of a lead/bonus page, and the
// view counters lived in per-ORIGIN sessionStorage under a bare key. A domain
// hosting several funnels ('/' and '/ngos1/') therefore shared one counter:
// a customer who saw funnel A's bonus1 and then reached funnel B's without
// passing B's index — which is the only thing that resets the counters — landed
// on count 2, so B's sale postback was silently skipped. Key them per funnel.
//
// A session already in flight when this ships has a legacy unscoped value and no
// scope marker. Adopt that value rather than restarting at 0, or a customer who
// reloads bonus1 across the deploy fires a DUPLICATE sale postback.
// Each funnel gets its OWN counter, rather than one counter plus a marker for
// whoever touched it last: with a shared counter, hopping A -> B -> A resets A
// and fires A's postback a SECOND time. Duplicated conversions are worse than
// the missed ones this is fixing.
const viewCounterKey = (key) => `${key}:${getFunnelScope(window.location.pathname)}`;

const readViewCounter = (key) => {
	const scoped = viewCounterKey(key);
	let raw = sessionStorage.getItem(scoped);

	if (raw === null) {
		// A session that started on <= 1.2.1 has an unscoped counter. Hand it to
		// the first funnel that asks and drop it: that customer does not re-fire a
		// postback they already fired, and every other funnel still starts clean.
		const legacy = sessionStorage.getItem(key);
		if (legacy !== null) {
			raw = legacy;
			sessionStorage.removeItem(key);
		}
	}

	const current = parseInt(raw, 10);
	return Number.isFinite(current) ? current : 0;
};

const bumpViewCounter = (key) => {
	const next = readViewCounter(key) + 1;
	sessionStorage.setItem(viewCounterKey(key), next);
	return next;
};

const resetViewCounter = (key) => {
	sessionStorage.removeItem(viewCounterKey(key));
	sessionStorage.removeItem(key); // legacy unscoped counter
};

const applyAutocompleteAttributes = (formSelector) => {
	const autocompleteMap = {
		cShipFName: 'given-name',
		cShipLName: 'family-name',
		cBillFName: 'given-name',
		cBillLName: 'family-name',
		cEmail: 'email',
		cPhone2: 'tel',
		cShipAddress1: 'address-line1',
		cShipAddress2: 'address-line2',
		cShipCity: 'address-level2',
		cShipState: 'address-level1',
		cShipZip: 'postal-code',
		cShipCountry: 'country',
		cBillAddress1: 'address-line1',
		cBillAddress2: 'address-line2',
		cBillCity: 'address-level2',
		cBillState: 'address-level1',
		cBillZip: 'postal-code',
		cBillCountry: 'country',
	};
	Object.keys(autocompleteMap).forEach((id) => {
		$(`${formSelector} #${id}`).attr('autocomplete', autocompleteMap[id]);
	});
};

const render = () => {

	debug('Starting rendering the page');
	// Set to the presell request on index pages; see the checkout-page click.
	let presellReady;
	debug('Rendered offer page config', getConfig());

	/* ---------------- init before all scripts will run ---------------- */

	const queryParams = new URLSearchParams(window.location.search);
	var fieldsSetArray = [];

	// Unchanged from the original inline loop: an existing field gets its value
	// set, an unknown one is appended as a hidden input. This is how inbound
	// third-party links prefill the form (discount codes, chash from
	// cart-abandonment email), so the behaviour is deliberately preserved.
	const hydrateField = (key, value) => {
		if (!$('form').length) return;
		if (!$("#" + key).length) {
			$('form').append(`<input type="hidden" name="${key}" id="${key}" value="${value}" />`);
		} else {
			debug(key + " exists. Setting the value");
			$("#" + key).val(value);
		}
		fieldsSetArray.push(key);
	};

	// Landing on the funnel entry means a new run: drop any handoff left over
	// from a previous pass, so re-entering via select never lands on a shipping
	// form pre-filled with the last customer's details.
	if (isIndex()) {
		deleteSessionStorage(HANDOFF_STORAGE_KEY);
	}

	// The previous step's PII, handed over out-of-band so it never touches the
	// URL. Applied BEFORE the query string so an explicit param still wins —
	// test links and inbound campaign URLs keep overriding, as they always did.
	//
	// Only the page the handoff was addressed to may consume it; otherwise it
	// would re-apply on every page for the rest of the session.
	const handoff = sessionStorage.getItem(HANDOFF_STORAGE_KEY);
	if (handoff) {
		try {
			const stored = JSON.parse(handoff);
			// `scope` is absent on a handoff written by <= 1.2.1, mid-session
			// across a deploy — accept those rather than stranding the customer
			// on an empty checkout.
			const scopeMatches = stored.scope === undefined
				|| stored.scope === getFunnelScope(window.location.pathname);
			if (stored && stored.target === getCurrentPageName() && scopeMatches && stored.fields) {
				Object.keys(stored.fields).forEach((key) => hydrateField(key, stored.fields[key]));
			}
		} catch (e) {
			debug("Unreadable PII handoff, ignoring: " + e);
		}
	}

	for (let item of queryParams.entries()) {
		const [key, value] = item;
		hydrateField(key, value);
	}
	if (fieldsSetArray.length) {
		debug("Fields were set: " + fieldsSetArray);
	}

	if ($('#shipping_form').length) {
		if (!$('#cShipCountry').length) {
			$('#shipping_form').append('<input type="hidden" name="cShipCountry" id="cShipCountry" value="US" />');
		}

		if (!$('#cBillProductIndex').length) {
			$('#shipping_form').append('<input type="hidden" name="cBillProductIndex" id="cBillProductIndex" value="0" />');
		} else {
			if (!$('#cBillProductIndex').val()) {
				$('#cBillProductIndex').val(0);
			}
		}

		// konnektive-core configs only: the delivery-frequency pick that
		// subscribeProduct.js reads. Same field and default as core's render().
		if (getConfig().coreConfig) {
			if (!$('#cBillSubscribeIndex').length) {
				$('#shipping_form').append('<input type="hidden" name="cBillSubscribeIndex" id="cBillSubscribeIndex" value="0" />');
			} else if (!$('#cBillSubscribeIndex').val()) {
				$('#cBillSubscribeIndex').val(0);
			}
		}

		if (!$('#cBillPurchaseType').length) {
			$('#shipping_form').append('<input type="hidden" name="cBillPurchaseType" id="cBillPurchaseType" value="onetime" />');
		}

		if (!$('#cBillPromoCode').length) {
			$('#shipping_form').append('<input type="hidden" name="cBillPromoCode" id="cBillPromoCode" value="" />');
		}

		if (!$('#cShipAddress2').length) {
			$('#shipping_form').append('<input type="hidden" name="cShipAddress2" id="cShipAddress2" value="" />');
		}

		applyAutocompleteAttributes('#shipping_form');
		debug("Form processed: shipping_form");
	}

	if ($('#payment_form').length) {
		if (!$('#cBillCountry').length) {
			$('#payment_form').append('<input type="hidden" name="cBillCountry" id="cBillCountry" value="US" />');
		}

		if (!$('#cShipCountry').length) {
			$('#payment_form').append('<input type="hidden" name="cShipCountry" id="cShipCountry" value="US" />');
		}

		if ($('#cShipCountry').length) {
			$('#cBillCountry').val($('#cShipCountry').val());
		}

		if (!$('#cBillAddress2').length) {
			$('#payment_form').append('<input type="hidden" name="cBillAddress2" id="cBillAddress2" value="" />');
		}

		if (!$('#cBillPurchaseType').length) {
			$('#payment_form').append('<input type="hidden" name="cBillPurchaseType" id="cBillPurchaseType" value="onetime" />');
		}
		if (!$('#cBillProductIndex').length) {
			$('#payment_form').append('<input type="hidden" name="cBillProductIndex" id="cBillProductIndex" value="0" />');
		} else {
			if (!$('#cBillProductIndex').val()) {
				$('#cBillProductIndex').val(0);
			}
		}

		$('#payment_form').append('<input type="hidden" name="order_id" id="order_id" value="" /><input type="hidden" name="orderTotal" id="orderTotal" value="" />');
		applyAutocompleteAttributes('#payment_form');
		debug("Form processed: payment_form");
	}

	$('[data-rel="priceShipping"]').html(getConfig().priceShipping);
	$('[data-rel="priceRebill"]').html(getConfig().priceRebill);
	$('[data-rel="priceRetail"]').html(getConfig().priceRetail);
	$('[data-rel="phoneNumber"]').html(getConfig().servicePhone);
	$('[data-rel="phoneNumberCall"]').attr("href", "tel:" + getConfig().servicePhone);
	$('[data-rel="productName"]').html(getConfig().productName);
	$('[data-rel="companyName"]').html(getConfig().companyName);
	// konnektive-core slot (1.5.1); a page without the element is unaffected.
	$('[data-rel="subscribeDiscount"]').html(getConfig().discountSubscribe);

	if(getConfig().termsData && getConfig().termsData.length){ // let's auto apply terms misc data
		$.each(getConfig().termsData, (tKey,tValue) =>{
			$('[data-rel="' + tValue.key + '"]').html(tValue.value);
		})				
	}

	Date.prototype.addDays = function(days) { var date = new Date(this.valueOf());
		date.setDate(date.getDate() + days); return date; }
	const date = new Date();
	const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

	$('[data-rel="dateYear"]').html(new Date().getFullYear());
	$('[data-rel="dateTerms"]').html(date.addDays(18).toLocaleDateString("en-US", dateOptions));
	$('[data-rel="dateArrival"]').html(date.addDays(4).toLocaleDateString("en-US", dateOptions));
	$('[data-rel="dateShipping"]').html(date.addDays(3).toLocaleDateString("en-US", dateOptions));

	/*  ---------------------------  VARIABLES  ---------------------------  */
	let chTPT;
	let lpUrl;
	let sessionId;
	const servicePopups = '<div class="overlay"></div><div class="processing"></div>';
	const qs = {};

	const expiryDate = new Date();

	/** *****************************************************
	  SAVE TO SESSION STORAGE
	****************************************************** */

	expiryDate.setMonth(expiryDate.getMonth() + 1);

	// let's determine lp_url each time no matter of cookies

	if (qs !== undefined) {
		if (qs.ver !== undefined) {
			lpUrl = `${document.location.pathname.replace(/^[/]?/, '').split('/')[0]}-v${qs.ver}`;
		} else {
			[lpUrl] = document.location.pathname.replace(/^[/]?/, '').split('/');
		}
	} else {
		[lpUrl] = document.location.pathname.replace(/^[/]?/, '').split('/');
	}

	if (!lpUrl || lpUrl.indexOf('.') != -1 || lpUrl == 'checkout') {
		lpUrl = 'root';
	}

	setCookieValue('lp_url', lpUrl);
	setSessionStorage('lp_url', lpUrl);

	debug('OP URL: ', lpUrl);
	debug('Full path:', document.location.pathname);

	// Used to grab query string params
	if (document.location.search) {
		$.each(document.location.search.substr(1).split('&'), (c, q) => {
			const i = q.split('=');
			qs[i[0].toString()] = i[1].toString();
		});
	}

	$('#ship_bill').click(function() {
		if ($(this).is(':checked')) {
			$('#biling__details').slideUp();
			$('input[name=billEqualShip]').val('Y');
		} else {
			$('#biling__details').slideDown();
			$('input[name=billEqualShip]').val('N');
		}
	});

	let years = new Date().getFullYear();
	const futureYear = years + 10;
	const cardYears = [];
	let cardYearOptions = '<option>Year</option>';
	let i;
	for (years; years <= futureYear; years += 1) {
		cardYears.push(years);
	}
	for (i = 0; i < cardYears.length; i += 1) {
		cardYearOptions += `<option value="${cardYears[i]}">${cardYears[i]}</option>`;
	}
	$('select[id="ccYear"]').append(cardYearOptions);

	if ($('#ccMonth').length) {
		$('#ccMonth').html(MONTHS_BILL_SELECT);
	}

	if ($('#cShipState').length) {
		setStates('cShipState', null);
	}

	if ($('#cBillState').length) {
		setStates('cBillState', $('#cShipState').val());
	}

	$('body').append(servicePopups);
	$('head').append(`<style>${UTILS_CSS}</style>`);

	$(window).bind('pageshow', () => {
		if (getCurrentPageName() !== 'thankyou' && getConfig().instance !== "ordermanager" && getConfig().instance !== "sapphire") {
			resetFormState();
		}

	});

	if (isIndex()) {
		setSessionStorage('ordersList', '');
		deleteSessionStorage('konnektiveSessionID');
		deleteSessionStorage('quick_lead_order_id');
		deleteSessionStorage('konnektiveAffVals');
		deleteSessionStorage('konnektiveLeadData');
		deleteSessionStorage('konnektivePurchaseData');
		deleteSessionStorage('konnektiveOrderData');

		// A new checkout needs a fresh gateway session and idempotency key —
		// reusing a consumed session fails, and a stale key replays the
		// previous order.
		clearGatewaySession();
		clearOrderIdempotencyKey();

		sessionStorage.removeItem('imagesList');
		deleteSessionStorage('prospect_id');
		deleteSessionStorage('customer_age');
		deleteSessionStorage('customer_gender');
		deleteSessionStorage('customer_issue_type');
		deleteSessionStorage('customer_issue_value');

		// Entering a funnel by its index starts that funnel's journey over —
		// but only that funnel's. A sibling funnel's counters are left alone.
		resetViewCounter('bonusViews');
		resetViewCounter('leadViews');

//		setSessionStorage('refd', getQueryParamValue('refd')); // setting referrer domain for RT postback domain
//		setSessionStorage('trk', getQueryParamValue('trk')); // setting referrer domain for RT postback domain

		if (getConfig().provider == "konnektive") {
			presellReady = doPresellPage();
		}
	}

	if ($('#shipping_form').length) {
		if (getConfig().provider == "konnektive") {
			if (useCheckoutGateway()) {
				// Same race as the checkout-page click below, on index pages that
				// carry the shipping form: wait so the gateway's
				// konnektiveSessionID is the one that sticks.
				$.when(presellReady).always(doLeadPage);
			} else {
				doLeadPage();
			}
		}
	}

	if ($('#payment_form').length) {
		appendShippingInfo();

		var leadViews = bumpViewCounter('leadViews');

		debug("RTDOMAIN: " + getRTDomain(getQueryParamValue('refd'), getQueryParamValue('trk')));
		if (getConfig().rtPostbacks && getQueryParamValue('clickid') && leadViews == 1) {
			firePostBack({
				debug: pageDebugFlag(),
				domain: getRTDomain(getQueryParamValue('refd'), getQueryParamValue('trk')), //getConfig().rtDomain,
				type: 'lead',
				sum: 0,
				clickid: getQueryParamValue('clickid'),
				// Field first, query second. cEmail is withheld from the URL
				// under urlPiiTransport: 'session', so reading the query alone
				// posts an empty email — silently, with a 200 back. render()
				// hydrates #cEmail from the handoff well before this point.
				email: shipValue('cEmail')
			});
		}

		if (getConfig().provider == "konnektive") {
			konnektive_get_images();
		}

		if (getConfig().provider == "konnektive") {
			if (useCheckoutGateway()) {
				// One-page funnels run presell and the checkout click on the same
				// page. Both write konnektiveSessionID; wait so the gateway's
				// session is the one that sticks. always(): a failed presell (an
				// error response, or a network failure that shows nothing) must
				// not block the click.
				$.when(presellReady).always(doCheckoutPage);
			} else {
				doCheckoutPage();
			}
		}
	}

	$('#payment_form').append(`<input type="hidden" id="urlparams" name="urlparams" value="${window.location.search.substring(1)}">`);
	
	// applying the params filter
	if($('#urlparams').length){
		$('#urlparams').val('');
		var re = /([^&=]+)=?([^&]*)/g;
		var decodeRE = /\+/g;  // Regex for replacing addition symbol with a space
		var decode = function (str) { return decodeURIComponent( str.replace(decodeRE, " ") ); };
		var params = {}, e;
		while ( e = re.exec(window.location.search.substring(1)) ) params[ decode(e[1]) ] = decode( e[2] );
		$.each(params, (pI, pV) => {
			if(getConfig().passThruParams.indexOf(pI) != -1){
				$('#urlparams').val($('#urlparams').val() + pI + '=' + pV + '&');
			}	else {
			}
		});
			if( $('#urlparams').val()[ $('#urlparams').val().length - 1] == '&' ){
			$('#urlparams').val($('#urlparams').val().substring(0, $('#urlparams').val().length - 1));
		}
	}
	// applying the params filter
	
	
	$('body').addClass('kaxsdc');
	$('body').attr('data-event', 'load');

	if (getConfig().useKount) {
		initKount();
	}

	const ordersList = getOrdersList();

	// Sapphire cart (1.6.1), as demo-engine's render() did: draw the saved
	// cart and the product grid into the page's Sapphire templates.
	if (isSapphireMode()) {
		getSapphireCart().loadCart();
		sapphireCartOutput();
		sapphireDisplayProducts();
		bindSapphireAddToCartButton();
	}

	// Sapphire mode also treats its configured thank-you page
	// (sapphireThankYouPage, e.g. page-thank-you.php) as the thank-you page.
	switch (isSapphireThankYouPage() ? 'thankyou' : getCurrentPageName()) {
		case 'bonus1': // bonus1 handlings
			var purchaseResponseInfo = $.parseJSON(sessionStorage.getItem('konnektivePurchaseData'));
			var bonusViews = bumpViewCounter('bonusViews');

			// `purchaseResponseInfo` is null whenever bonus1 is reached without a
			// completed order in this session — a direct link, a bookmark, a crawler.
			// Reading .totalAmount off it threw and aborted the rest of render();
			// and a *sale* postback should not fire when no sale happened anyway.
			if (getConfig().rtPostbacks && bonusViews == 1 && purchaseResponseInfo) {
				var savedProductIndex = 0;
				if(sessionStorage.getItem('selectedIndex')){
					savedProductIndex = sessionStorage.getItem('selectedIndex');
				}
				var rtTotal = purchaseResponseInfo.totalAmount;

				if(getConfig().products[savedProductIndex].isTrial) {
					rtTotal = '';
				}
				
				debug("RTDOMAIN: " + getRTDomain(getQueryParamValue('refd'), getQueryParamValue('trk')));
				firePostBack({
					debug: pageDebugFlag(),
					domain: getRTDomain(getQueryParamValue('refd'), getQueryParamValue('trk')), //getConfig().rtDomain,
					type: 'sale',
					sum: rtTotal,
					clickid: getQueryParamValue('clickid'),
					orderid: getQueryParamValue('orderID')
				});
			}

			resetDebugCounter();
			break;

		case 'thankyou': // TY page handlings
			$('.overlay, .processing').show();
			deleteSessionStorage('order_id');
			deleteSessionStorage('test_order');
			deleteSessionStorage('country_env');
			resetViewCounter('bonusViews');
			deleteSessionStorage(HANDOFF_STORAGE_KEY); // PII should not outlive the conversion

			if (getConfig().provider == "konnektive") {
				konnektive_finalize_orders();
			}

			clearCookies(['msclkid']);
			$('html').removeClass('placeholder');
			break;
		default: // rest of the pages
			break;
	}

	var detectedBonusNumber = getBonusNumber(getCurrentPageName()); // handle all bonusN pages
	if (detectedBonusNumber !== null) {
		setConfig({ upsellIndex: detectedBonusNumber - 1 });
		setDefaultBonusActions();
		$('.offserSteps').html("Offer " + detectedBonusNumber + " of " + getConfig().upsells.length);
	}

	if (window.location.search.substring(1)) {
		if ($('a').length) {
			$('a').each(function() {
				const ahref = $(this).attr('href');
				if (ahref) {
					if (ahref.indexOf('http') !== -1 || ahref.indexOf('javascript') !== -1) {
						// empty
					} else if (ahref !== '#' && ahref.indexOf('#') !== 0) {
						$(this).attr('href', `${$(this).attr('href')}?${window.location.search.substring(1)}`);
					}
				}
			});
		}

		if ($('form').length) {
			if ($('form').attr('action')) {
				if ($('form').attr('action').indexOf('?') === -1) {
					$('form').attr('action', `${$('form').attr('action')}?${window.location.search.substring(1)}`);
				}
			}
		}
	}

	$('.actualYear').html(new Date().getFullYear());
	$('.phoneNumber').html(getConfig().servicePhone);
};

export default render;